import React from 'react'
import type { OrderDetails } from '@/shared/api'

const DASH = '—'

const formatText = (value: string | null | undefined): string =>
  value && value.trim() ? value : DASH

const formatMoney = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return DASH
  const amount = Number(value)
  if (Number.isNaN(amount)) return value
  return `${amount.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ₽`
}

const formatCount = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) return DASH
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 3 })
}

/**
 * Произвольное значение из JSON-разбивки тарифа.
 * Показываем только то, что пришло с backend, без домысливания подписей.
 */
const formatBreakdownValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return DASH
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) {
    const parts = value.map(formatBreakdownValue).filter((part) => part !== DASH)
    return parts.length > 0 ? parts.join(', ') : DASH
  }
  if (typeof value === 'object') {
    const parts = Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${key}: ${formatBreakdownValue(item)}`)
      .filter((part) => !part.endsWith(`: ${DASH}`))
    return parts.length > 0 ? parts.join(', ') : DASH
  }
  return String(value)
}

interface FieldProps {
  label: string
  children: React.ReactNode
  className?: string
}

const Field: React.FC<FieldProps> = ({ label, children, className }) => (
  <div className={className}>
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd className="mt-1 break-words text-sm text-gray-900">{children}</dd>
  </div>
)

const Section: React.FC<{
  title: string
  children: React.ReactNode
}> = ({ title, children }) => (
  <section className="rounded-lg border border-gray-200 p-4">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    <div className="mt-3">{children}</div>
  </section>
)

export interface OrderInfoTabProps {
  order: OrderDetails
}

/**
 * Вкладка «Информация»: только чтение, все отсутствующие значения — «—».
 */
export const OrderInfoTab: React.FC<OrderInfoTabProps> = ({ order }) => {
  const sender = order.sender
  const recipient = order.recipient
  const tariff = order.courierTariff

  return (
    <div className="space-y-4">
      <Section title="Основное">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Дата создания">
            {formatText(order.createdAtFormatted)}
          </Field>
          <Field label="Дата доставки">
            {formatText(order.deliveryDateFormatted)}
          </Field>
          <Field label="Интервал доставки">
            {formatText(order.deliveryInterval?.label)}
          </Field>
          <Field label="Адрес доставки">{formatText(order.address)}</Field>
        </dl>
      </Section>

      <Section title="Отправитель">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Название">{formatText(sender.name)}</Field>
          <Field label="Контактное лицо">
            {formatText(sender.contactPerson)}
          </Field>
          <Field label="Телефон">{formatText(sender.phone)}</Field>
          <Field label="Адрес склада">{formatText(sender.address)}</Field>
          {order.storage && (
            <>
              <Field label="Номер склада">
                {formatText(
                  order.storage.number !== null
                    ? String(order.storage.number)
                    : null
                )}
              </Field>
              <Field label="Адрес склада (справочник)">
                {formatText(order.storage.address)}
              </Field>
            </>
          )}
        </dl>
      </Section>

      <Section title="Получатель">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Имя">{formatText(recipient.name)}</Field>
          <Field label="Телефон">{formatText(recipient.phone)}</Field>
          <Field label="Адрес" className="sm:col-span-2">
            {formatText(recipient.address)}
          </Field>
        </dl>
      </Section>

      <Section title="Состав заказа">
        {order.items.length === 0 ? (
          <p className="text-sm text-gray-500">{DASH}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {order.items.map((item, index) => (
              <li
                key={`item-${index}`}
                className="flex items-start justify-between gap-4 py-2 first:pt-0 last:pb-0"
              >
                <span className="break-words text-sm text-gray-900">
                  {formatText(item.name)}
                </span>
                <span className="whitespace-nowrap text-sm text-gray-500">
                  {item.count !== null ? `× ${formatCount(item.count)}` : DASH}
                </span>
              </li>
            ))}
          </ul>
        )}

        {order.goods.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Грузовые места
            </h4>
            <ul className="mt-2 divide-y divide-gray-100">
              {order.goods.map((good, index) => (
                <li key={`good-${index}`} className="space-y-1 py-2 first:pt-0 last:pb-0">
                  <div className="break-words text-sm text-gray-900">
                    {formatText(good.name)}
                  </div>
                  {good.address && (
                    <div className="break-words text-xs text-gray-500">
                      {good.address}
                    </div>
                  )}
                  {good.qr && (
                    <div className="break-all text-xs text-gray-500">
                      QR: {good.qr}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section title="Оплата и комментарий">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Способ оплаты">{formatText(order.paymentTypeName)}</Field>
          <Field label="Комментарий курьеру" className="sm:col-span-2">
            {formatText(order.comment)}
          </Field>
        </dl>
      </Section>

      <Section title="Финансы">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Цена клиента">{formatMoney(order.clientPrice)}</Field>
          <Field label="Вознаграждение курьера">
            {formatMoney(order.courierReward)}
          </Field>
          <Field label="Маржа">{formatMoney(order.margin)}</Field>
        </dl>
      </Section>

      <Section title="Тариф курьера">
        {!tariff ? (
          <p className="text-sm text-gray-500">Тариф не зафиксирован</p>
        ) : (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Тариф">
              {tariff.courierTariffId !== null
                ? `№ ${tariff.courierTariffId}`
                : DASH}
            </Field>
            <Field label="Версия тарифа">
              {tariff.courierTariffVersionId !== null
                ? String(tariff.courierTariffVersionId)
                : DASH}
            </Field>
            <Field label="Сумма">{formatMoney(tariff.amount)}</Field>
            <Field label="Зафиксирован">
              {formatText(tariff.frozenAtFormatted)}
            </Field>
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Разбивка
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {tariff.breakdown.length === 0 ? (
                  DASH
                ) : (
                  <ul className="list-inside list-disc space-y-1">
                    {tariff.breakdown.map((entry, index) => (
                      <li key={`breakdown-${index}`} className="break-words">
                        {formatBreakdownValue(entry)}
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
          </dl>
        )}
      </Section>
    </div>
  )
}
